import React, { useState, useEffect } from "react";
import axios from "axios";
import "./OrderBook.css";
import { FaPlus, FaTimes } from "react-icons/fa";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate } from "react-router-dom";
import AlertModal from "../AlertModal/AlertModal";
import config from "../../config";
import useAuth from "../../Auth/useAuth";

const getTruckDataUrl = `${config.BASE_URL}/api/trucks`;
const getVendorDataUrl = `${config.BASE_URL}/api/vendors`;
const getDestinationDataUrl = `${config.BASE_URL}/api/destination`;
const getOrderBookDataUrl = `${config.BASE_URL}/list-order-book`;
const addOrderDataUrl = `${config.BASE_URL}/add-order`;

const OrderBook = () => {
  const Navigate = useNavigate();

  const {
    username,
    userProfile,
    setActiveModule,
    setTripIDState,
    setModuleChange,
  } = useAuth();
  const [orderBookData, setOrderBookData] = useState([]);
  const [time, setTime] = useState(new Date());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    date: "",
    time: "",
    truckNumber: "",
    truckType: "",
    vendor: "",
    tdsRate: "",
    destinationFrom: "",
    destinationTo: "",
    rate: "",
  });

  // Dropdown data states
  const [truckNumbers, setTruckNumbers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [allFromOptions, setAllFromOptions] = useState([]);
  const [allToOptions, setAllToOptions] = useState([]);
  const [filteredFromOptions, setFilteredFromOptions] = useState([]);
  const [filteredToOptions, setFilteredToOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Add modal state
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'info'
  });

  // Modal handler functions
  const showModal = (title, message, type = 'info') => {
    setModalConfig({
      isOpen: true,
      title,
      message,
      type
    });
  };

  const closeModal = () => {
    setModalConfig(prev => ({ ...prev, isOpen: false }));
  };

  useEffect(() => {
    sessionStorage.setItem("actMod", "order-book");
    setActiveModule(sessionStorage.getItem("actMod"));
    fetchOrderBookData();
    fetchDropdownData();
  }, []);

  const fetchOrderBookData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get(getOrderBookDataUrl, {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      });
      console.log("orderBookData:>", response.data);
      if (response.data?.length > 0) {
        setOrderBookData(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching order book data:", error);
      setOrderBookData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDropdownData = async () => {
    setIsLoading(true);
    try {
      // Fetch truck numbers
      const truckResponse = await axios.get(getTruckDataUrl, {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      });
      console.log("truckNumbers:>", truckResponse.data.truckData);
      setTruckNumbers(truckResponse.data.truckData || []);

      // Fetch vendors
      const vendorResponse = await axios.get(getVendorDataUrl, {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      });
      console.log("vendors:>", vendorResponse.data.vendorData);
      // Sort by company_name (A–Z)
      const sortedVendors = (vendorResponse.data.vendorData || []).sort(
        (a, b) => a.company_name.localeCompare(b.company_name)
      );

      setVendors(sortedVendors);

      // Fetch destinations
      const response = await axios.get(getDestinationDataUrl, {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      });
      console.log("destinationData:>", response.data.destinationData);
      const destinationList = response.data.destinationData || [];
      setDestinations(destinationList);

      // Extract all unique from/to values
      const uniqueFroms = [
        ...new Set(destinationList.map((dest) => dest.from)),
      ].sort();
      const uniqueTos = [
        ...new Set(destinationList.map((dest) => dest.to)),
      ].sort();

      setAllFromOptions(uniqueFroms);
      setAllToOptions(uniqueTos);
      setFilteredFromOptions(uniqueFroms);
      setFilteredToOptions(uniqueTos);
    } catch (error) {
      console.error("Error fetching destinations:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Field labels mapping for error messages
  const fieldLabels = {
    date: "Date",
    time: "Time",
    truckNumber: "Truck Number",
    truckType: "Truck Type",
    vendor: "Vendor",
    tdsRate: "TDS Rate",
    destinationFrom: "Destination From",
    destinationTo: "Destination To",
    rate: "Rate"
  };

  // Validation function
  const validateForm = () => {
    const requiredFields = [
      'date',
      'time',
      'truckNumber',
      'truckType',
      'vendor',
      'destinationFrom',
      'destinationTo',
      'rate'
    ];

    // Special check for tdsRate - it should have a value even if it's 0
    if (formData.tdsRate === "" || formData.tdsRate === null || formData.tdsRate === undefined) {
      return { isValid: false, missingField: 'tdsRate' };
    }

    for (const field of requiredFields) {
      if (!formData[field] || formData[field.trim()] === "") {
        return { isValid: false, missingField: field };
      }
    }

    return { isValid: true, missingField: null };
  };

  // Handle vendor selection to auto-fill TDS Rate
  const handleVendorChange = (companyName) => {
    console.log(
      "Selected vendor Company Name:",
      companyName,
      "\nVendors:",
      vendors
    );
    // Find the vendor by company_name since that's what's used as the select option value
    const selectedVendor = vendors.find(
      (vendor) => vendor.company_name === companyName
    );
    console.log("Selected vendor:", selectedVendor);
    setFormData((prev) => ({
      ...prev,
      vendor: companyName, // Store company name
      tdsRate: selectedVendor ? selectedVendor.tds_rate : "", // Auto-fill TDS
    }));
  };

  // Handle destination dependencies
  const handleDestinationFromChange = (fromValue) => {
    setFormData((prev) => ({
      ...prev,
      destinationFrom: fromValue,
    }));

    if (!fromValue) {
      setFilteredToOptions(allToOptions);
    } else {
      // Filter to options based on selected from
      const validTos = destinations
        .filter(
          (dest) =>
            dest.from === fromValue ||
            (dest.mappedTo && dest.mappedTo.includes(fromValue))
        )
        .map((dest) => dest.to);

      setFilteredToOptions([...new Set(validTos)]);
    }

    // Attempt to find matching rate if both values are selected
    const match = destinations.find(
      (d) => d.from === fromValue && d.to === formData.destinationTo
    );
    if (match) {
      setFormData((prev) => ({ ...prev, rate: match.rate }));
    } else {
      setFormData((prev) => ({ ...prev, rate: "" })); // Clear rate if no match
    }
  };

  const handleDestinationToChange = (toValue) => {
    // Update form data without resetting the other field yet
    setFormData((prev) => ({
      ...prev,
      destinationTo: toValue,
    }));

    if (!toValue) {
      // If cleared, show all from options
      setFilteredFromOptions(allFromOptions);
    } else {
      // Filter from options based on selected to
      const validFroms = destinations
        .filter(
          (dest) =>
            dest.to === toValue ||
            (dest.mappedFrom && dest.mappedFrom.includes(toValue))
        )
        .map((dest) => dest.from);

      setFilteredFromOptions([...new Set(validFroms)]);
    }

    // Attempt to find matching rate if both values are selected
    const match = destinations.find(
      (d) => d.to === toValue && d.from === formData.destinationFrom
    );
    if (match) {
      setFormData((prev) => ({ ...prev, rate: match.rate }));
    } else {
      setFormData((prev) => ({ ...prev, rate: "" })); // Clear rate if no match
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddOrder = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setFormData({
      date: "",
      time: "",
      truckNumber: "",
      truckType: "",
      vendor: "",
      tdsRate: "",
      destinationFrom: "",
      destinationTo: "",
      rate: "",
    });
    setTime(new Date()); // Reset time picker as well
  };

  const handleTimeChange = (selectedTime) => {
    setTime(selectedTime);
    setFormData({
      ...formData,
      time: selectedTime.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      }),
    });
  };

  // Check if selected date is today
  const isToday = (dateString) => {
    if (!dateString) return false;
    const selectedDate = new Date(dateString);
    const today = new Date();
    return (
      selectedDate.getDate() === today.getDate() &&
      selectedDate.getMonth() === today.getMonth() &&
      selectedDate.getFullYear() === today.getFullYear()
    );
  };

  // Get rounded time (next 30 minute interval)
  const getRoundedTime = () => {
    const now = new Date();
    const minutes = now.getMinutes();
    const roundedMinutes = minutes < 30 ? 30 : 60;
    now.setMinutes(roundedMinutes, 0, 0);
    return now;
  };

  const handleSubmitOrder = async () => {
    setIsSubmitting(true);
    try {
      // Validate form before submission
      const validation = validateForm();
      if (!validation.isValid) {
        const fieldName = fieldLabels[validation.missingField];
        showModal("Error", `Please fill "${fieldName}" field.`, "error");
        setIsSubmitting(false);
        return;
      }

      const orderData = {
        ...formData,
        username,
        profile: userProfile,
      };
      console.log("Order Data:", orderData);

      console.log("Before adding order-------------------------");
      const response = await axios.post(addOrderDataUrl, orderData, {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      });

      if (response.data) {
        fetchOrderBookData();
        handleCloseModal();
        showModal("Success", "Order scheduled successfully!", "success");
      }
    } catch (error) {
      console.error("Error scheduling order:", error);
      setError("Failed to schedule order. Please try again.");
      showModal("Error", "Failed to schedule order. Please try again.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const convertTime = (time) => {
    // This function assumes time is in a format like "HH:MM:SS AM/PM"
    // and extracts "HH:MM AM/PM".
    // If 'time' from API is always in a specific format (e.g., 'HH:MM:SS'),
    // you might need to adjust this for proper AM/PM display.
    if (!time) return "";
    const parts = time.split(":");
    if (parts.length >= 2) {
      let hour = parseInt(parts[0]);
      const minute = parts[1];
      const ampm = hour >= 12 ? "PM" : "AM";
      hour = hour % 12;
      hour = hour ? hour : 12; // The hour '0' should be '12'
      return `${hour}:${minute} ${ampm}`;
    }
    return time; // Return original if format is unexpected
  };

  return (
    <div className="order-book-container">
      <AlertModal
        isOpen={modalConfig.isOpen}
        onClose={closeModal}
        title={modalConfig.title}
        message={modalConfig.message}
        type={modalConfig.type}
      />
      {/* You could add a loading indicator here for the initial user fetch too */}
      {isLoading && (
        <div className="spinner-container">
          <div className="spinner"></div>
          <span>Loading...</span>
        </div>
      )}
      {!isLoading && (
        <div className="order-book-operations">
          <button className="order-book-button" onClick={handleAddOrder}>
            <FaPlus /> New Order
          </button>
        </div>
      )}
      {error && !isModalOpen && (
        <div style={{ color: "red", textAlign: "center", marginTop: "1rem" }}>
          {error}
        </div>
      )}

      {isModalOpen && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Schedule New Order</h3>
              <button className="close-button" onClick={handleCloseModal}>
                <FaTimes />
              </button>
            </div>

            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>Date *</label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => handleInputChange("date", e.target.value)}
                    min={new Date().toISOString().split("T")[0]}
                    className="form-control"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Time *</label>
                  <div className="DatePicker">
                    <DatePicker
                      selected={time}
                      onChange={handleTimeChange}
                      showTimeSelect
                      showTimeSelectOnly
                      timeCaption="Time"
                      dateFormat="h:mm aa"
                      className="form-control"
                      required
                      minTime={
                        isToday(formData.date)
                          ? getRoundedTime()
                          : new Date().setHours(0, 0, 0)
                      }
                      maxTime={new Date().setHours(23, 59, 59)}
                      filterTime={(time) => {
                        if (isToday(formData.date)) {
                          const now = new Date();
                          return time.getTime() > now.getTime();
                        }
                        return true;
                      }}
                      injectTimes={[new Date()]}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Truck Number *</label>
                  <select
                    value={formData.truckNumber}
                    onChange={(e) =>
                      handleInputChange("truckNumber", e.target.value)
                    }
                    className="form-control"
                    required
                  >
                    <option value="">Select Truck Number</option>
                    {truckNumbers.map((truck) => (
                      <option key={truck._id} value={truck.truck_no}>
                        {truck.truck_no}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Truck Type *</label>
                  <select
                    value={formData.truckType}
                    onChange={(e) =>
                      handleInputChange("truckType", e.target.value)
                    }
                    className="form-control"
                    required
                  >
                    <option value="">Select Truck Type</option>
                    <option value="Bulker">Bulker</option>
                    <option value="Bag">Bag</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Vendor *</label>
                  <select
                    value={formData.vendor}
                    onChange={(e) => handleVendorChange(e.target.value)}
                    className="form-control"
                    required
                  >
                    <option value="">Select Vendor</option>
                    {vendors.length > 0 &&
                      vendors.map((vendor, index) => (
                        <option
                          key={vendor._id || index}
                          value={vendor.company_name}
                        >
                          {vendor.company_name}
                        </option>
                      ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>TDS Rate *</label>
                  <input
                    type="text"
                    value={formData.tdsRate}
                    onChange={(e) =>
                      handleInputChange("tdsRate", e.target.value)
                    }
                    className="form-control"
                    readOnly
                    placeholder="Auto-filled based on vendor"
                  />
                </div>

                <div className="form-group">
                  <label>Destination From *</label>
                  <select
                    value={formData.destinationFrom}
                    onChange={(e) =>
                      handleDestinationFromChange(e.target.value)
                    }
                    className="form-control"
                    required
                  >
                    <option value="">Select Destination From</option>
                    {filteredFromOptions.map((from, index) => (
                      <option key={`from-${index}`} value={from}>
                        {from}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Destination To *</label>
                  <select
                    value={formData.destinationTo}
                    onChange={(e) => handleDestinationToChange(e.target.value)}
                    className="form-control"
                    required
                  >
                    <option value="">Select Destination To</option>
                    {filteredToOptions.map((to, index) => (
                      <option key={`to-${index}`} value={to}>
                        {to}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Rate *</label>
                  <input
                    type="text"
                    value={formData.rate}
                    onChange={(e) => handleInputChange("rate", e.target.value)}
                    className="form-control"
                    readOnly
                    placeholder="Auto-filled on Destinations"
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-cancel" onClick={handleCloseModal}>
                Cancel
              </button>
              <button 
                className="btn-schedule" 
                onClick={handleSubmitOrder}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Scheduling..." : "Schedule Order"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="order-book-table-wrapper">
        {isLoading && (
          <div className="modal-spinner-overlay">
            <div className="spinner"></div>
            <span>Loading orders...</span>
          </div>
        )}

        {!isLoading && orderBookData.length > 0 ? (
          <div className="order-book-scrollable-table">
            <table className="order-book-table">
              <thead>
                <tr>
                  <th>Trip ID</th>
                  <th>Username</th>
                  <th>Date</th>
                  <th>Time</th>
                  <th>Truck No.</th>
                  <th>Truck Type</th>
                  <th>Vendor</th>
                  <th>TDS</th>
                  <th>From</th>
                  <th>To</th>
                  <th>Rate</th>
                  <th>Transaction</th>
                </tr>
              </thead>
              <tbody>
                {orderBookData.map((order) => (
                  <tr key={order._id}>
                    {/* <td>{order.TripID}</td> */}
                    <td
                      onClick={async () => {
                        let tripId = order.TripID;
                        setModuleChange("/home");
                        const response = await axios.get(
                          `/api/trip/${tripId}`,
                          {
                            withCredentials: true,
                            headers: {
                              "Content-Type": "application/json",
                            },
                          }
                        );
                        console.log("TripData", response.data.TripData);
                        setTripIDState(response.data.TripData);
                        Navigate(`/home/${tripId}`);
                      }}
                      className="trip-id"
                    >
                      {order.TripID}
                    </td>
                    <td>{order.Username}</td>
                    <td>{order.localDate}</td>
                    <td>{convertTime(order.localTime)}</td>
                    <td>{order.TruckNumber}</td>
                    <td>{order.TruckType}</td>
                    <td>{order.Vendor}</td>
                    <td>{order.TDS_Rate}</td>
                    <td>{order.DestinationFrom}</td>
                    <td>{order.DestinationTo}</td>
                    <td>{order.Rate}</td>
                    <td>{order.TransactionStatus}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          !isLoading &&
          orderBookData.length === 0 && (
            <div className="no-data-message">No Order Book data found.</div>
          )
        )}
      </div>
    </div>
  );
};

export default OrderBook;